import { useState, useEffect, useRef } from 'react';
import { Camera, Upload, X, Car, Search, ChevronLeft, Image, Loader2, GripVertical, Move, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast, Toaster } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MobileUpload = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pin, setPin] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [lastUploadCount, setLastUploadCount] = useState(0);
  
  // Drag state for new photos
  const [draggedNewIndex, setDraggedNewIndex] = useState(null);
  const [dragOverNewIndex, setDragOverNewIndex] = useState(null);
  
  // Drag state for existing photos
  const [draggedExistingIndex, setDraggedExistingIndex] = useState(null);
  const [dragOverExistingIndex, setDragOverExistingIndex] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const pinInputRef = useRef(null);

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem('photo_token');
    if (token) {
      setIsAuthenticated(true);
      fetchVehicles(token);
    } else {
      setLoading(false);
    }
  }, []);

  const handlePinLogin = async (e) => {
    e.preventDefault();
    if (pin.length < 4) return;
    setLoggingIn(true);
    try {
      const response = await axios.post(`${API}/auth/pin-login`, { pin });
      localStorage.setItem('photo_token', response.data.access_token);
      setIsAuthenticated(true);
      fetchVehicles(response.data.access_token);
      toast.success('Connexion reussie!');
    } catch (error) {
      toast.error('PIN invalide');
      setPin('');
    } finally {
      setLoggingIn(false);
    }
  };

  const fetchVehicles = async (token) => {
    try {
      const response = await axios.get(`${API}/vehicles?limit=200&include_offline=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = Array.isArray(response.data) ? response.data : response.data.vehicles || [];
      data.sort((a, b) => (a.stock_number || '').localeCompare(b.stock_number || ''));
      setVehicles(data);
      setFilteredVehicles(data);
    } catch (error) {
      toast.error('Erreur de chargement des vehicules');
      if (error.response?.status === 401) {
        localStorage.removeItem('photo_token');
        setIsAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter vehicles based on search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredVehicles(vehicles);
      return;
    }
    const query = searchQuery.toLowerCase();
    const filtered = vehicles.filter(v => 
      v.stock_number?.toLowerCase().includes(query) ||
      v.make?.toLowerCase().includes(query) ||
      v.model?.toLowerCase().includes(query) ||
      String(v.year).includes(query)
    );
    setFilteredVehicles(filtered);
  }, [searchQuery, vehicles]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploadSuccess(false);
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (index) => {
    setPhotos(prev => {
      const newPhotos = [...prev];
      URL.revokeObjectURL(newPhotos[index].preview);
      newPhotos.splice(index, 1);
      return newPhotos;
    });
  };

  // Drag handlers for NEW photos
  const handleNewPhotoDragStart = (e, index) => {
    setDraggedNewIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleNewPhotoDragOver = (e, index) => {
    e.preventDefault();
    if (draggedNewIndex !== null && draggedNewIndex !== index) setDragOverNewIndex(index);
  };
  const handleNewPhotoDragLeave = () => setDragOverNewIndex(null);
  const handleNewPhotoDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedNewIndex === null || draggedNewIndex === dropIndex) {
      setDraggedNewIndex(null); setDragOverNewIndex(null); return;
    }
    setPhotos(prev => {
      const arr = [...prev];
      const [item] = arr.splice(draggedNewIndex, 1);
      arr.splice(dropIndex, 0, item);
      return arr;
    });
    setDraggedNewIndex(null); setDragOverNewIndex(null);
  };
  const handleNewPhotoDragEnd = () => { setDraggedNewIndex(null); setDragOverNewIndex(null); };

  // Drag handlers for EXISTING photos
  const handleExistingPhotoDragStart = (e, index) => {
    setDraggedExistingIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleExistingPhotoDragOver = (e, index) => {
    e.preventDefault();
    if (draggedExistingIndex !== null && draggedExistingIndex !== index) setDragOverExistingIndex(index);
  };
  const handleExistingPhotoDragLeave = () => setDragOverExistingIndex(null);
  const handleExistingPhotoDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (draggedExistingIndex === null || draggedExistingIndex === dropIndex) {
      setDraggedExistingIndex(null); setDragOverExistingIndex(null); return;
    }
    const existingImages = getExistingImages();
    const newImages = [...existingImages];
    const [item] = newImages.splice(draggedExistingIndex, 1);
    newImages.splice(dropIndex, 0, item);
    setSavingOrder(true);
    try {
      const token = localStorage.getItem('photo_token');
      await axios.put(`${API}/vehicles/${selectedVehicle.id}`, { images: newImages }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedVehicle(prev => ({ ...prev, images: newImages }));
      setVehicles(prev => prev.map(v => v.id === selectedVehicle.id ? { ...v, images: newImages } : v));
      toast.success('Ordre mis a jour!');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingOrder(false);
    }
    setDraggedExistingIndex(null); setDragOverExistingIndex(null);
  };
  const handleExistingPhotoDragEnd = () => { setDraggedExistingIndex(null); setDragOverExistingIndex(null); };

  // Delete existing photo
  const deleteExistingPhoto = async (index) => {
    const existingImages = getExistingImages();
    const newImages = existingImages.filter((_, i) => i !== index);
    setSavingOrder(true);
    try {
      const token = localStorage.getItem('photo_token');
      await axios.put(`${API}/vehicles/${selectedVehicle.id}`, { images: newImages }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedVehicle(prev => ({ ...prev, images: newImages }));
      setVehicles(prev => prev.map(v => v.id === selectedVehicle.id ? { ...v, images: newImages } : v));
      toast.success('Photo supprimee!');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setSavingOrder(false);
    }
  };

  const getExistingImages = () => {
    return (selectedVehicle?.images || []).filter(img => 
      img && !img.includes('placeholder') && !img.includes('customer-assets')
    );
  };

  const goBackToList = () => {
    setSelectedVehicle(null);
    setPhotos([]);
    setUploadSuccess(false);
    setLastUploadCount(0);
  };

  const uploadPhotos = async () => {
    if (!selectedVehicle || photos.length === 0) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadSuccess(false);
    
    const token = localStorage.getItem('photo_token');
    const formData = new FormData();
    photos.forEach(photo => formData.append('files', photo.file));
    
    try {
      const uploadResponse = await axios.post(`${API}/upload/images`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });
      
      const uploadedUrls = uploadResponse.data.uploaded.map(img => img.url);
      const existingImages = getExistingImages();
      const allImages = [...existingImages, ...uploadedUrls];
      
      await axios.put(`${API}/vehicles/${selectedVehicle.id}`, { images: allImages }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLastUploadCount(photos.length);
      setSelectedVehicle(prev => ({ ...prev, images: allImages }));
      setVehicles(prev => prev.map(v => v.id === selectedVehicle.id ? { ...v, images: allImages } : v));
      setPhotos([]);
      setUploadSuccess(true);
      toast.success(`${uploadedUrls.length} photo(s) ajoutee(s)!`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors du televersement');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // PIN Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0c2340] to-[#1e3a5f] flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Camera className="h-8 w-8 text-blue-600" />
            </div>
            <h1 className="text-xl font-bold text-slate-900">Photo Upload</h1>
            <p className="text-slate-500 text-sm">Entrepot Auto Montreal</p>
          </div>
          
          <form onSubmit={handlePinLogin} className="space-y-4">
            <div>
              <Input
                ref={pinInputRef}
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Entrez votre PIN"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                className="h-14 text-center text-2xl tracking-[0.5em] font-bold"
                maxLength={6}
                required
                data-testid="pin-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full h-12 text-base bg-[#0c2340] hover:bg-[#1e3a5f]"
              disabled={loggingIn || pin.length < 4}
              data-testid="pin-submit"
            >
              {loggingIn ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                'Connexion'
              )}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Loading Screen
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const existingImages = getExistingImages();

  // Vehicle Selected - Photo Capture Mode
  if (selectedVehicle) {
    return (
      <div className="min-h-screen bg-slate-100 pb-44">
        <Toaster position="top-center" richColors />
        
        {/* Header with back button */}
        <div className="bg-[#0c2340] text-white sticky top-0 z-10">
          <div className="flex items-center gap-3 p-4">
            <button 
              onClick={goBackToList}
              className="p-2 -ml-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              data-testid="back-to-list-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="font-bold text-lg truncate">
                {selectedVehicle.stock_number}
              </h1>
              <p className="text-white/70 text-sm truncate">
                {selectedVehicle.year} {selectedVehicle.make} {selectedVehicle.model}
              </p>
            </div>
            <span className="text-xs bg-white/20 px-2 py-1 rounded">
              {existingImages.length} photos
            </span>
          </div>
        </div>

        {/* Upload Success Banner */}
        {uploadSuccess && (
          <div className="mx-4 mt-4 bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-green-800">{lastUploadCount} photo(s) televersee(s)!</p>
                <p className="text-green-600 text-sm">Photos ajoutees a {selectedVehicle.stock_number}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={goBackToList}
                className="h-12 bg-[#0c2340] hover:bg-[#1e3a5f] text-white"
                data-testid="choose-another-btn"
              >
                <Car className="h-4 w-4 mr-2" />
                Autre vehicule
              </Button>
              <Button
                variant="outline"
                onClick={() => setUploadSuccess(false)}
                className="h-12"
              >
                <Camera className="h-4 w-4 mr-2" />
                Ajouter encore
              </Button>
            </div>
          </div>
        )}

        <div className="p-4 space-y-4">
          {/* New Photos Section (staged for upload) */}
          {photos.length > 0 && (
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Nouvelles photos ({photos.length})
                <span className="text-xs font-normal text-slate-400 ml-auto flex items-center gap-1">
                  <Move className="h-3 w-3" /> Glissez pour reordonner
                </span>
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {photos.map((photo, index) => (
                  <div 
                    key={index} 
                    draggable
                    onDragStart={(e) => handleNewPhotoDragStart(e, index)}
                    onDragOver={(e) => handleNewPhotoDragOver(e, index)}
                    onDragLeave={handleNewPhotoDragLeave}
                    onDrop={(e) => handleNewPhotoDrop(e, index)}
                    onDragEnd={handleNewPhotoDragEnd}
                    className={`relative aspect-square rounded-lg overflow-hidden bg-slate-200 cursor-move transition-all ${
                      draggedNewIndex === index ? 'opacity-50 scale-95' : ''
                    } ${
                      dragOverNewIndex === index ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                    }`}
                  >
                    <img src={photo.preview} alt={`Photo ${index + 1}`} className="w-full h-full object-cover pointer-events-none" />
                    <span className={`absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded font-bold ${
                      index === 0 ? 'bg-blue-600 text-white' : 'bg-black/60 text-white'
                    }`}>{index === 0 ? '1' : index + 1}</span>
                    <div className="absolute top-1 right-8 p-1 bg-white/80 rounded">
                      <GripVertical className="h-3 w-3 text-slate-600" />
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); removePhoto(index); }}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Existing Photos Section */}
          {existingImages.length > 0 && (
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Image className="h-4 w-4" />
                Photos actuelles ({existingImages.length})
                {savingOrder && <Loader2 className="h-4 w-4 animate-spin text-blue-500" />}
                <span className="text-xs font-normal text-slate-400 ml-auto flex items-center gap-1">
                  <Move className="h-3 w-3" /> Glissez pour reordonner
                </span>
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {existingImages.map((url, index) => (
                  <div 
                    key={url + index}
                    draggable
                    onDragStart={(e) => handleExistingPhotoDragStart(e, index)}
                    onDragOver={(e) => handleExistingPhotoDragOver(e, index)}
                    onDragLeave={handleExistingPhotoDragLeave}
                    onDrop={(e) => handleExistingPhotoDrop(e, index)}
                    onDragEnd={handleExistingPhotoDragEnd}
                    className={`relative aspect-square rounded-lg overflow-hidden bg-slate-200 cursor-move transition-all ${
                      draggedExistingIndex === index ? 'opacity-50 scale-95' : ''
                    } ${
                      dragOverExistingIndex === index ? 'ring-2 ring-green-500 ring-offset-2' : ''
                    }`}
                  >
                    <img src={url} alt={`Photo ${index + 1}`} className="w-full h-full object-cover pointer-events-none" />
                    <span className={`absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded font-bold ${
                      index === 0 ? 'bg-green-600 text-white' : 'bg-black/60 text-white'
                    }`}>{index === 0 ? '1' : index + 1}</span>
                    <div className="absolute top-1 right-8 p-1 bg-white/80 rounded">
                      <GripVertical className="h-3 w-3 text-slate-600" />
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); deleteExistingPhoto(index); }}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {photos.length === 0 && existingImages.length === 0 && !uploadSuccess && (
            <div className="bg-white rounded-xl p-8 shadow-sm text-center">
              <Camera className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">Aucune photo</p>
              <p className="text-slate-400 text-sm">Utilisez les boutons ci-dessous pour ajouter des photos</p>
            </div>
          )}
        </div>

        {/* Fixed Bottom Action Bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 space-y-3 z-20">
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" multiple onChange={handleFileSelect} className="hidden" />
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileSelect} className="hidden" />

          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" onClick={() => cameraInputRef.current?.click()} className="h-12 text-base" disabled={uploading}>
              <Camera className="h-5 w-5 mr-2" /> Camera
            </Button>
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="h-12 text-base" disabled={uploading}>
              <Image className="h-5 w-5 mr-2" /> Galerie
            </Button>
          </div>
          
          {photos.length > 0 && (
            <Button onClick={uploadPhotos} disabled={uploading} className="w-full h-12 text-base bg-green-600 hover:bg-green-700">
              {uploading ? (
                <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Televersement... {uploadProgress}%</>
              ) : (
                <><Upload className="h-5 w-5 mr-2" /> Televerser {photos.length} photo(s)</>
              )}
            </Button>
          )}

          {/* Prominent back button always visible */}
          <Button variant="ghost" onClick={goBackToList} className="w-full h-10 text-sm text-slate-500 hover:text-slate-700" data-testid="bottom-back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Choisir un autre vehicule
          </Button>
        </div>
      </div>
    );
  }

  // Vehicle Selection Screen
  return (
    <div className="min-h-screen bg-slate-100">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <div className="bg-[#0c2340] text-white sticky top-0 z-10">
        <div className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
              <Camera className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Photos Vehicules</h1>
              <p className="text-white/60 text-xs">Selectionnez un vehicule</p>
            </div>
          </div>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Rechercher par stock, marque, modele..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 text-base bg-white"
              data-testid="vehicle-search"
            />
          </div>
        </div>
      </div>

      {/* Vehicle List */}
      <div className="p-4 space-y-2">
        <p className="text-sm text-slate-500 mb-2">{filteredVehicles.length} vehicule(s)</p>
        {filteredVehicles.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Car className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>Aucun vehicule trouve</p>
          </div>
        ) : (
          filteredVehicles.map(vehicle => {
            const realPhotos = (vehicle.images || []).filter(img => 
              img && !img.includes('placeholder') && !img.includes('customer-assets')
            );
            const hasPhotos = realPhotos.length > 0;
            
            return (
              <button
                key={vehicle.id}
                onClick={() => { setSelectedVehicle(vehicle); setUploadSuccess(false); }}
                className="w-full bg-white rounded-xl p-3 flex items-center gap-3 shadow-sm active:shadow-md active:scale-[0.99] transition-all text-left"
                data-testid={`vehicle-${vehicle.stock_number}`}
              >
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-200 flex-shrink-0">
                  {hasPhotos ? (
                    <img src={realPhotos[0]} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                      <Camera className="h-6 w-6" />
                    </div>
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-blue-600">{vehicle.stock_number || 'N/A'}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      hasPhotos ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {hasPhotos ? `${realPhotos.length} photos` : 'Sans photo'}
                    </span>
                  </div>
                  <p className="text-slate-700 truncate">{vehicle.year} {vehicle.make} {vehicle.model}</p>
                  <p className="text-slate-500 text-sm truncate">{vehicle.exterior_color} {vehicle.mileage ? `- ${vehicle.mileage.toLocaleString()} km` : ''}</p>
                </div>
                
                <ChevronLeft className="h-5 w-5 text-slate-400 rotate-180 flex-shrink-0" />
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MobileUpload;
